<template>
  <modal
    :class="`create-form-modal ${step === 1 ? 'gray-bg full-height' : ''}`"
    :show="show"
    :title="title"
    @close="close"
  >
    <template v-if="step === 1">
      <div class="fields">
        <transition-group name="field-transition" tag="div" class="fields-list">
          <div class="field" v-for="(field, index) in data.fields" :key="`${field.type}${index}`">
            <div class="inner">
              <div class="summary">
                <h2>{{ field.label }}</h2>
                <p>{{ printFieldType(field.type) }}</p>
              </div>
              <div class="actions">
                <div class="remove-or-edit">
                  <div @click="editField = editField === index ? null : index">
                    <i class="icon-edit-pen blue" />
                  </div>
                  <div @click="field.type !== 'email' ? removeField(index) : null">
                    <i :class="`icon-trash1 ${field.type !== 'email' ? 'red' : 'gray' }`" />
                  </div>
                </div>
                <div class="move">
                  <div @click="move(index, true)">
                    <i :class="`icon-arrow-move-up ${index === 0 ? 'gray' : 'blue'}`" />
                  </div>
                  <div @click="move(index, false)">
                    <i :class="`icon-arrow-move-down ${index === data.fields.length - 1 ? 'gray' : 'blue' }`" />
                  </div>
                </div>
              </div>
            </div>
            <div class="edit-field d-f fd-c f-g-8" :data-active="editField === index" :ref="editField === index ? 'editField' : null">
              <template v-if="editField === index">
                <div class="d-f">
                  <div class="f-2">
                    <p class="m-b-1"><strong>Label</strong></p>
                    <form-input v-model="data.fields[editField].label"  />
                  </div>
                  <div class="m-l-2">
                    <p class="m-b-1"><strong>Required</strong></p>
                    <form-checkbox :disabled="field.type === 'email'" :selected="data.fields[editField].required" @input="data.fields[editField].required = !data.fields[editField].required" icon="check" />
                  </div>
                </div>
                <div>
                </div>
                <template v-if="field.type === 'dropdown'">
                  <div>
                    <p class="m-b-1"><strong>Answers</strong></p>
                    <form-input
                      v-model="data.fields[editField].values[valueIndex]"
                      v-for="(value, valueIndex) in field.values"
                      :key="valueIndex"
                      :placeholder="`Add value for ${convertToNth(valueIndex)} option`"
                    />
                  </div>
                </template>
              </template>
            </div>
          </div>
        </transition-group>

        <template v-if="newField">
          <div class="add-new-field">
            <p>Add field</p>
            <ul>
              <li @click="addFieldType('name')">Name</li>
              <li @click="addFieldType('phone')">Phone number</li>
              <li @click="addFieldType('dropdown')">Life of choices</li>
              <li @click="addFieldType('text')">Custom text field</li>
            </ul>
            <span @click="newField = false">Cancel</span>
          </div>
        </template>
        <template v-else>
          <div class="add-field" @click="newField = true;">
            <i class="icon-plusCircle" /> Add field
          </div>
        </template>
      </div>
    </template>
    <template v-if="step === 2">
      <div class="p-a-4 d-f fd-c f-g-8">
        <div>
          <p class="m-b-1"><strong>Title</strong></p>
          <form-input v-model="data.title"  />
        </div>
        <div>
          <p class="m-b-1"><strong>Description</strong></p>
          <form-input v-model="data.description"  />
        </div>
      </div>
    </template>
    <template v-if="step === 0 && form">
      <div class="p-a-4 border-line d-f ai-c form-summary">
        <div>
          <strong>{{ form.title }}</strong>
          <p>{{ form.description }}</p>
        </div>
        <i class="icon-edit-pen2 ml-a" @click="step = 2" />
      </div>
      <div class="p-a-4 border-line d-f ai-c form-summary">
        <div>
          <strong>Form fields</strong>
          <p>
            {{ form.fields.length }} fields in total -
            <template v-for="(field, index) in form.fields">{{ index !== 0 ? index === form.fields.length - 1 ? ' and ' : ', ' : '' }}<span :key="index">{{ field.label }}</span></template>
          </p>
        </div>
        <template v-if="form.state === 'created'">
          <i class="icon-edit-pen2 ml-a" @click="step = 1" />
        </template>
      </div>
    </template>

    <template v-slot:footer>
      <div class="d-f f-g-8 f-w p-y-4" v-if="[1, 2].includes(step)">
        <template v-if="step === 1">
          <Button @click="form ? save() : step = 2" class="f-w">{{ form ? 'Save fields' : 'Continue' }}</Button>
        </template>
        <template v-if="step === 2">
          <Button @click="step = 1" class="outline f-w" v-if="!form">Back to fields</Button>
          <Button @click="save()" class="f-w">{{ form ? 'Save information' : 'Create form' }}</Button>
        </template>
      </div>
      <template v-if="step === 0">
        <template v-if="form">
          <template v-if="justCreated">
            <div class="f-w p-y-4">
              <template>
                <div class="d-f m-b-2 f-g-8">
                  <Button class="ml-a f-w" @click="publishForm" v-if="form.state === 'created'">Open for submissions now</Button>
                </div>
              </template>
              <div class="d-f f-g-8">
                <Button class="outline f-w" @click="close">Open for submissions later</Button>
              </div>
              <p class="p-x-4 p-t-4 ta-c gray-text">If you choose to publish now, viewers will see it directly. If you choose later, it will not be shown to viewers until you choose to publish it.</p>
            </div>
          </template>
          <template v-else>
            <div class="f-w p-y-4">
              <template v-if="form.state === 'created'">
                <Button class="ml-a f-w m-b-2" @click="publishForm">Open for submissions</Button>
              </template>
              <template v-if="['published', 'completed'].includes(form.state)">
                <div class="d-f m-b-2 f-g-8">
                  <Button class="f-w" @click="goToSubmissions($event, form)">{{ form.submissions?.length || 0 }} Submission{{ (form.submissions?.length || 0) === 0 || (form.submissions?.length || 0) > 1 ? 's' : '' }}</Button>
                </div>
              </template>
              <div class="d-f f-g-8">
                <Button class="outline f-w" @click="completeForm" v-if="form.state === 'published'">Close for submissions</Button>
                <Button class="outline red f-w" icon="trash1" @click="deleteForm">Delete</Button>
              </div>
            </div>
          </template>
        </template>
      </template>
    </template>
  </modal>
</template>

<script>
export default {
  computed: {
    /**
     * Return the current broadcast in store
     * @returns {?Object}
     */
    broadcast() {
      return this.$store.getters['Broadcast/current'];
    },
    form() {
      return this.$store.getters['Forms/form'];
    },
    create() {
      return this.$store.getters['Forms/create'];
    },
    edit() {
      return this.form.state !== 'published';
    },
    show() {
      return !!this.form || this.create;
    },
    title() {
      if (!this.form) {
        if (this.step === 2) {
          return 'What should we call it?';
        }

        return 'Create new form';
      }

      if (this.form.state !== 'published') {
        return 'This is your form!';
      }

      return this.form.title;
    },
  },
  methods: {
    convertToNth(num) {
      const nths = ['first', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh', 'eighth', 'ninth', 'tenth', 'eleventh', 'twelfth', 'thirteenth', 'fourteenth', 'fifteenth', 'sixteenth', 'seventeenth', 'eighteenth', 'nineteenth'];
      return nths[num];
    },
    printFieldType(type) {
      switch (type) {
        case 'email':
          return 'E-mail address';
        case 'name':
          return 'Name';
        case 'phone':
          return 'Phonenumber';
        case 'dropdown':
          return 'List of choices';
        case 'text':
          return 'Custom text';
        default:
          break;
      }

      return '';
    },
    move(index, up) {
      const item = this.data.fields[index];
      this.data.fields.splice(index, 1);
      this.data.fields.splice(up ? index - 1 : index + 1, 0, item);
    },
    close() {
      this.justCreated = false;
      this.$store.commit('Forms/toggleCreate', false);
      this.$store.commit('Forms/setForm', null);
    },
    addFieldType(type) {
      this.data.fields.push({
        label: 'New field',
        required: false,
        type,
        values: type === 'dropdown' ? [''] : null,
      });

      this.editField = this.data.fields.length - 1;

      this.$nextTick(() => {
        try {
          const [field] = this.$refs.editField;
          field.querySelector('input').select();
          field.querySelector('input').focus();
        } catch (e) {
          // Do nothing
        }
      });

      this.newField = false;
    },
    goToSubmissions(event, form) {
      this.close();
      this.handleLink(event, `/form/${form.id}`);
    },
    removeField(index) {
      this.data.fields.splice(index, 1);
    },
    removeDropdownValue(fieldIndex, valueIndex) {
      this.data.fields[fieldIndex].values.splice(valueIndex, 1);
    },
    addDropdownValue(index) {
      this.data.fields[index].values.push(this.dropdownValue);
      this.dropdownValue = '';
    },
    async publishForm() {
      if (this.state !== '') {
        return;
      }

      this.justCreated = false;

      const updateState = await swal({
        title: this.$t('common.are_you_sure'),
        text: 'Viewers can submit answers to your form, but you will no longer be able to edit the forms fields.',
        buttons: ['Cancel', 'OK, Open for submissions'],
        className: 'center-content',
      });

      if (!updateState) {
        return;
      }

      const bool = await this.$store.dispatch('Forms/updateForm', {
        id: this.form.id,
        payload: {
          state: 'published',
        },
      });

      await this.$store.dispatch('Forms/getForms', {
        id: this.broadcast.id,
      });

      if (!bool) {
        swal({ title: 'An error occured', text: 'Could not publish form', icon: 'error' });
      }
    },
    async completeForm() {
      if (this.state !== '') {
        return;
      }

      const willClose = await swal({
        title: this.$t('errors.are_you_sure'),
        text: 'Viewers will not be able to submit new answers after the form is closed',
        icon: 'warning',
        buttons: ['Cancel', 'Complete form'],
        dangerMode: true,
      });

      if (!willClose) {
        return;
      }

      const bool = await this.$store.dispatch('Forms/updateForm', {
        id: this.form.id,
        payload: {
          state: 'completed',
        },
      });

      if (!bool) {
        swal({ title: 'An error occured', text: 'Could not complete form', icon: 'error' });
      }
    },
    async deleteForm() {
      if (this.state !== '') {
        return;
      }

      const willDelete = await swal({
        title: this.$t('errors.are_you_sure'),
        text: 'This will remove the form and all submitted answers',
        icon: 'warning',
        buttons: ['Cancel', 'Delete form'],
        dangerMode: true,
      });

      if (!willDelete) {
        return;
      }

      const bool = await this.$store.dispatch('Forms/deleteForm', this.form.id);
      if (!bool) {
        swal({ title: 'An error occured', text: 'Could not delete form', icon: 'error' });
        return;
      }

      await this.$store.dispatch('Forms/getForms', {
        id: this.broadcast.id,
      });

      this.close();
    },
    async save() {
      const data = JSON.parse(JSON.stringify(this.data));
      for (let i = 0; i < data.fields.length; i += 1) {
        if (data.fields[i].type === 'dropdown') {
          data.fields[i].values = data.fields[i].values.filter(value => value !== '');
        }
      }

      if (this.create) {
        await this.$store.dispatch('Forms/createForm', {
          broadcast_id: this.broadcast.id,
          ...data,
        });
        this.justCreated = true;
        this.$store.commit('Forms/toggleCreate', false);
        this.step = 0;

        await this.$store.dispatch('Forms/getForms', {
          id: this.broadcast.id,
        });

        return;
      }

      await this.$store.dispatch('Forms/updateForm', {
        id: this.form.id,
        payload: this.data,
      });
      this.step = 0;

      await this.$store.dispatch('Forms/getForms', {
        id: this.broadcast.id,
      });
    },
  },
  watch: {
    show(bool) {
      if (!bool) {
        this.newField = false;
        this.editField = null;
        this.step = 0;
        this.data = {
          title: '',
          description: '',
          // eslint-disable-next-line
          fields: [{ label: 'E-mail', required: true, type: 'email', values: null }],
        };
      }
    },
    create(bool) {
      this.step = bool ? 1 : 0;
    },
    data: {
      deep: true,
      handler(d) {
        d.fields.forEach((field, index) => {
          if (field.type === 'dropdown') {
            if (field.values[field.values.length - 1] !== '') {
              this.data.fields[index].values.push('');
            }
          }
        });
      },
    },
    form(form) {
      if (!form) {
        this.data = {
          title: '',
          description: '',
          // eslint-disable-next-line
          fields: [{ label: 'E-mail', required: true, type: 'email', values: null }],
        };
        return;
      }

      this.data = {
        title: this.form.title,
        description: this.form.description,
        fields: this.form.fields,
      };
    },
  },
  data() {
    return {
      data: {
        title: '',
        description: '',
        // eslint-disable-next-line
        fields: [{ label: 'E-mail', required: true, type: 'email', values: null }],
      },
      newField: false,
      editField: null,
      step: 0,
      state: '',
      stateTimer: '',
      justCreated: false,
    };
  },
  beforeDestroy() {
    clearTimeout(this.stateTimer);
  },
};
</script>

<style lang="scss">

.field-transition-move {
  transition: transform 0.5s ease;
}
.field-transition-enter-active, .field-transition-leave-active {
  transition: opacity 1s, transform 1s;
}
.field-transition-enter, .field-transition-leave-to {
  opacity: 0;
  transform: translateX(-100px);
}

.create-form-modal {
  .fields {
    display: flex;
    flex-direction: column;
    padding: 24px;
    gap: 12px;

    .fields-list {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .field {
      background: #FFFFFF;
      box-shadow: 0 2px 4px 0 rgba(165,179,194,0.20), 0 0 1px 0 rgba(165,179,194,0.50);
      border-radius: 4px;

      .inner {
        display: flex;
      }

      .edit-field {
        max-height: 0px;
        overflow: hidden;
        transition: max-height .5s;

        &[data-active="true"] {
          border-top: 1px solid rgba(165,179,194,0.20);
          max-height: 500px;
          padding: 16px 20px;
        }
      }

      .summary {
        padding: 16px 20px;

        h2 {
          font-size: 16px;
          font-weight: 600;
        }

        p {
          font-size: 12px;
          color: #727479;
        }
      }
      .actions {
        display: flex;
        margin-left: auto;

        .gray {
          color: var(--color-cta-gray);
          pointer-events: none;
        }

        .blue {
          color: var(--color-cta-blue);
        }

        .red {
          color: var(--color-cta-red);
        }

        .remove-or-edit {
          display: flex;

          & > div {
            border-left: 1px solid #EEF0F9;
            display: flex;
            align-items: center;
            padding: 0 12px;

            i {
              font-size: 20px;
            }

            &:hover {
              cursor: pointer;
            }
          }
        }

        .move {
          display: flex;
          flex-direction: column;
          padding: 0;
          border-left: 1px solid #EEF0F9;

          & > div {
            align-items: center;
            display: flex;
            flex: 1;
            padding: 0 12px;

            &:hover {
              cursor: pointer;
            }

            &:last-child:not(:only-child) {
              border-top: 1px solid #EEF0F9;
            }
          }
        }
      }
    }

    .add-field {
      align-items: center;
      border: 1px solid rgba(0,96,255,0.30);
      border-radius: 4px;
      color: #0060FF;
      display: flex;
      font-weight: 600;
      justify-content: center;
      padding: 20px 0;

      i {
        font-size: 20px;
        margin-right: 8px;
      }

      &:hover {
        cursor: pointer;
      }
    }

    .add-new-field {
      align-items: center;
      border: 1px solid rgba(0,96,255,0.30);
      border-radius: 4px;
      display: flex;
      flex-direction: column;
      padding: 20px;

      p {
        color: black;
        font-weight: 600;
      }

      ul {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
        margin-top: 16px;

        & > * {
          background-color: #0060FF;
          border-radius: 5px;
          color: white;
          width: calc(50% - 16px);
          padding: 12px;
          text-align: center;
          font-weight: 600;

          &:hover {
            cursor: pointer;
          }
        }
      }

      span {
        color: var(--color-cta-red);
        display: block;
        font-weight: 500;
        margin-top: 16px;

        &:hover {
          cursor: pointer;
        }
      }
    }
  }

  .form-summary {
    i {
      color: #0060FF;
      font-size: 24px;
    }

    p {
      font-size: 12px;
      color: #727479;
      line-height: 18px;
    }
  }
}
</style>
